import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar, Button, Card, Col, Input, Popconfirm, Row, Select,
  Statistic, Table, Tag, Typography, message, Space,
} from 'antd';
import { CheckOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { getAdminRedeemBalanceApi, markRedeemPaidApi } from '../../api/admin';
import { formatDate, formatCurrency } from '../../utils/formatters';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'paid',            label: 'Paid' },
  { value: 'all',             label: 'All' },
];

const SORT_OPTIONS = [
  { value: 'pending_amount',   label: 'Pending Amount' },
  { value: 'total_earned',     label: 'Total Earned' },
  { value: 'name',             label: 'Name' },
  { value: 'mobile',           label: 'Mobile' },
  { value: 'last_redeemed_at', label: 'Last Redeemed' },
];

export default function AdminRedeemBalance() {
  const [loading, setLoading]     = useState(false);
  const [marking, setMarking]     = useState(null);
  const [summary, setSummary]     = useState(null);
  const [data, setData]           = useState([]);
  const [meta, setMeta]           = useState({ total: 0, current_page: 1, per_page: 20 });
  const [status, setStatus]       = useState('pending_payment');
  const [sortBy, setSortBy]       = useState('pending_amount');
  const [search, setSearch]       = useState('');

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { status, sort_by: sortBy, page, per_page: meta.per_page };
      if (search) params.search = search;
      const res  = await getAdminRedeemBalanceApi(params);
      const body = res.data;
      setSummary(body.summary || null);
      setData(body.data || []);
      setMeta(body.meta || meta);
    } catch {
      message.error('Failed to load redeem requests');
    } finally {
      setLoading(false);
    }
  }, [status, sortBy, search, meta.per_page]);

  useEffect(() => { fetchData(1); }, [status, sortBy, search]);

  const handleMarkPaid = async (userId) => {
    setMarking(userId);
    try {
      const res = await markRedeemPaidApi({ user_id: userId });
      message.success(res.data?.message || 'Payment marked successfully');
      fetchData(meta.current_page);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to mark as paid');
    } finally {
      setMarking(null);
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <Space>
          <Avatar icon={<UserOutlined />} size={36} style={{ background: '#e8f5e9', color: '#1a6b2a', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.mobile}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Pending',
      dataIndex: 'pending_amount',
      width: 120,
      render: (v) => (
        <Text style={{ fontWeight: 700, color: '#ff4d4f' }}>{formatCurrency(v)}</Text>
      ),
    },
    {
      title: 'Paid',
      dataIndex: 'paid_amount',
      width: 110,
      render: (v) => <Text style={{ color: '#52c41a' }}>{formatCurrency(v)}</Text>,
    },
    {
      title: 'Total Earned',
      dataIndex: 'total_earned',
      width: 120,
      render: (v) => <Text style={{ fontWeight: 600 }}>{formatCurrency(v)}</Text>,
    },
    {
      title: 'Redemptions',
      key: 'redemptions',
      width: 130,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <div>Total: <b>{r.total_redemptions}</b></div>
          <div style={{ color: '#ff4d4f' }}>Pending: <b>{r.pending_redemptions}</b></div>
        </div>
      ),
    },
    {
      title: 'Last Redeemed',
      dataIndex: 'last_redeemed_at',
      width: 130,
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text> : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (v) => v === 'pending_payment'
        ? <Tag color="orange">Pending Payment</Tag>
        : <Tag color="green">Paid</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, r) => r.status === 'pending_payment' ? (
        <Popconfirm
          title={`Mark ₹${r.pending_amount} as paid for ${r.name}?`}
          onConfirm={() => handleMarkPaid(r.user_id)}
          okText="Yes, Mark Paid"
          okButtonProps={{ style: { background: '#1a6b2a', borderColor: '#1a6b2a' } }}
        >
          <Button
            size="small"
            icon={<CheckOutlined />}
            style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}
            loading={marking === r.user_id}
          >
            Mark Paid
          </Button>
        </Popconfirm>
      ) : (
        <Tag color="green">Paid</Tag>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Redeem Requests</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>Manage user balance withdrawals</Text>
        </div>
        <Space wrap>
          <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: 160 }} />
          <Select value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} style={{ width: 160 }} />
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search name, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 210 }}
          />
        </Space>
      </div>

      {summary && (
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Users',   value: summary.total_users,   color: '#1a6b2a', bg: '#f6ffed', prefix: '' },
            { label: 'Pending',       value: summary.total_pending, color: '#ff4d4f', bg: '#fff1f0', prefix: '₹' },
            { label: 'Paid',          value: summary.total_paid,    color: '#52c41a', bg: '#f6ffed', prefix: '₹' },
            { label: 'Grand Total',   value: summary.grand_total,   color: '#1677ff', bg: '#e6f4ff', prefix: '₹' },
          ].map((item) => (
            <Col xs={12} sm={6} key={item.label}>
              <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
                <Statistic
                  title={item.label}
                  value={item.value}
                  prefix={item.prefix}
                  valueStyle={{ color: item.color, fontSize: 20, fontWeight: 'bold' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="user_id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 1000 }}
          size="small"
          pagination={{
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (t) => `${t} users`,
            onChange: (page) => fetchData(page),
          }}
        />
      </Card>
    </div>
  );
}
